import { Component, Input, OnInit, OnChanges, SimpleChanges, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ChatService, ChatThread } from '../../../services/api/chat';
import { LanguageService } from '../../../services/i18n/language';
import { ChatComponent } from '../chat/chat.component';

/**
 * Список приватных тредов заявки (владелец↔каждый экспедитор) + открытый чат выбранного
 * треда — для владельца заявки и таможенного брокера (у экспедитора всегда один тред,
 * список ему не нужен — см. <app-chat> напрямую в его view-request).
 */
@Component({
  selector: 'app-chat-threads',
  standalone: true,
  imports: [CommonModule, ChatComponent],
  templateUrl: './chat-threads.component.html',
  styleUrl: './chat-threads.component.scss'
})
export class ChatThreadsComponent implements OnInit, OnChanges {
  @Input({ required: true }) requestId!: number;
  /** Заявка закрыта для новых сообщений (по ней уже заключена сделка). */
  @Input() locked = false;
  /** Экспедитор, чей тред открыть сразу — переход по клику на push-уведомление о новом
   *  сообщении (см. ChatService.notifyOnRequestMessage на бэкенде, параметр forwarderId
   *  в URL). Если такого треда нет в списке — просто игнорируется. */
  @Input() initialForwarderId: number | null = null;

  threads: ChatThread[] = [];
  loading = true;
  loadError: string | null = null;
  selectedForwarderId: number | null = null;

  titleText = 'Переписки с экспедиторами';
  emptyText = 'Пока никто из экспедиторов не написал по этой заявке';
  loadingText = 'Загрузка...';
  loadErrorText = 'Не удалось загрузить переписки';
  noSnippetText = '[вложение]';

  constructor(
    private chatService: ChatService,
    private languageService: LanguageService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.updateTranslations();
    this.languageService.getTranslations().subscribe(() => {
      this.updateTranslations();
      this.cdr.markForCheck();
    });
    this.load();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['requestId'] && !changes['requestId'].firstChange) {
      this.selectedForwarderId = null;
      this.load();
    }
  }

  private updateTranslations(): void {
    this.titleText = this.languageService.translate('chat.threadsTitle') || this.titleText;
    this.emptyText = this.languageService.translate('chat.threadsEmpty') || this.emptyText;
    this.loadingText = this.languageService.translate('common.loading') || this.loadingText;
    this.loadErrorText = this.languageService.translate('chat.threadsLoadError') || this.loadErrorText;
  }

  private load(): void {
    this.loading = true;
    this.loadError = null;
    this.chatService.getThreads(this.requestId).subscribe({
      next: (threads) => {
        // Треды с недавней перепиской — выше: чтобы было сразу видно, кто написал последним,
        // не перебирая весь список. Треды без сообщений (lastMessageAt не задан) — в конец.
        this.threads = [...threads].sort((a, b) => {
          const aTime = a.lastMessageAt ? new Date(a.lastMessageAt).getTime() : 0;
          const bTime = b.lastMessageAt ? new Date(b.lastMessageAt).getTime() : 0;
          return bTime - aTime;
        });
        this.loading = false;
        if (this.selectedForwarderId == null) {
          const wanted = this.initialForwarderId != null
            ? this.threads.find(t => t.forwarderId === this.initialForwarderId)
            : null;
          if (wanted) {
            this.selectedForwarderId = wanted.forwarderId;
          } else if (this.threads.length > 0) {
            this.selectedForwarderId = this.threads[0].forwarderId;
          }
        }
        this.cdr.markForCheck();
      },
      error: () => {
        this.loadError = this.loadErrorText;
        this.loading = false;
        this.cdr.markForCheck();
      }
    });
  }

  select(forwarderId: number): void {
    this.selectedForwarderId = forwarderId;
  }
}
